import useTranslate from '@src/i18n/useTranslate';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Input from '@src/components/ui2/Input';

import Button from '@src/components/ui2/Button';
import { MdOutlineCheckCircle } from 'react-icons/md';
import { Controller, FieldValues, useForm } from 'react-hook-form';
import type { PaymentMethodFormProps } from '@src/features/payments/options/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { directLinkCardFormSchema } from '@src/features/payments/legacy/directLinkCardFormSchema';
import { z } from 'zod';
import Select from '@src/components/ui2/Select';
import { useAppSelector } from '@src/core/hooks';
import { buyerIsForeign } from '@src/features/customers';
import { PaymentCardParams } from '@src/core/buyerApiClient';
import { CardBrandArt } from '@src/features/payments/components/CardBrandArt';
import styled from 'styled-components';
import { BodyRegularMedium } from '@src/fonts/typography/Typography';
import { LayoutColors } from '@src/core/theme';
import { getCepAddress } from '@src/common/auth';
import BackdropLoading from '@src/components/ui2/BackdropLoading';

export const DirectLinkCardForm = (props: PaymentMethodFormProps) => {
  const { paymentOption, onSubmit, contextErrors } = props;

  const [isLoading, setIsLoading] = useState(false);
  const { i18n } = useTranslate();

  const isForeignBuyer = useAppSelector(buyerIsForeign);

  const requiresExternalAuthentication = useMemo(() => paymentOption?.requiresExternalAuthentication, [paymentOption]);
  const formSchema = useMemo(
    () => directLinkCardFormSchema(i18n, isForeignBuyer, paymentOption),
    [i18n, isForeignBuyer, paymentOption],
  );
  type FormData = Omit<z.infer<typeof formSchema>, 'validityMonth' | 'validityYear'> & {
    validity: string;
  };

  const { handleSubmit, control, formState, setError, setValue } = useForm<FormData>({
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
    resolver: zodResolver(formSchema),
    defaultValues: {
      holder: '',
      holderIdentity: undefined,
      cardNumber: '',
      securityCode: '',
      validity: '',
      installments: paymentOption?.installmentOptions[0]!.division,
      billingAddress: {
        street: null,
        number: null,
        complement: null,
        district: null,
        city: null,
        state: null,
        zipCode: '',
      },
    },
  });
  const { errors } = formState;

  const getCardNumberError = () => {
    const formError = errors['cardNumber']!;
    if (formError) {
      return formError.message;
    }

    const contextError = contextErrors['paymentCard.cardNumber'];
    if (contextError && Array.isArray(contextError)) {
      return contextError.join(', ');
    }

    return null;
  };

  const getAddressByCep = useCallback(async (zipCode: string) => {
    if (isForeignBuyer) return;

    try {
      setIsLoading(true);
      const brasilApiAddress = await getCepAddress((zipCode ?? '').replace(/[^0-9]/g, ''));

      if (brasilApiAddress) {
        setValue('billingAddress.state', brasilApiAddress.state ?? '');
        setValue('billingAddress.city', brasilApiAddress.city ?? '');
        setValue('billingAddress.district', brasilApiAddress.district ?? '');
        setValue('billingAddress.street', brasilApiAddress.street ?? '');
        setValue('billingAddress.number', brasilApiAddress.number ?? '');
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const submit = (payload: FieldValues) => {
    const cardValues: PaymentCardParams = {
      holder: payload['holder'],
      holderIdentity: payload['holderIdentity'],
      cardNumber: payload['cardNumber'],
      securityCode: payload['securityCode'],
      validityMonth: payload['validityMonth'],
      validityYear: payload['validityYear'],
      installments: payload['installments'],
      billingAddress: payload['billingAddress'],
    };

    onSubmit(paymentOption.paymentMethod.id, cardValues);
  };

  const convertContextErrors = () => {
    for (const [key, value] of Object.entries(contextErrors)) {
      const match = /\[(\d+)\]\[([^\]]*)\]/.exec(key);

      if (match) {
        const field = match[2];

        for (const error of value) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          setError(field, {
            type: 'manual',
            message: error,
          });
        }
      }

      if (key === 'paymentCard') {
        console.log('paymentCard', value);
        setError('cardNumber', {
          type: 'manual',
          message: value.join(', '),
        });
      }
    }
  };

  useEffect(() => {
    convertContextErrors();
  }, [contextErrors]);

  return (
    <form onSubmit={handleSubmit(submit)}>
      <Controller
        name='holder'
        control={control}
        render={({ field: { onChange, value, name, ...rest } }) => (
          <Input
            name={name}
            value={value}
            onChange={onChange}
            label={i18n('checkout_nameAsInCard')}
            error={!!errors[name]}
            helperText={errors[name]?.message ?? i18n('direct_link_card_form_holder_helper')}
            {...rest}
          />
        )}
      />

      {!isForeignBuyer ? (
        <Controller
          name='holderIdentity'
          control={control}
          render={({ field: { onChange, value, name, ...rest } }) => (
            <Input
              name={name}
              mask={['cpf', 'cnpj']}
              value={value}
              onChange={onChange}
              label={i18n('checkout_card_owner_cpf_cnpj')}
              error={!!errors[name]}
              helperText={errors[name]?.message as string}
              {...rest}
            />
          )}
        />
      ) : null}

      {!isForeignBuyer && !requiresExternalAuthentication ? (
        <Controller
          name='billingAddress.zipCode'
          control={control}
          render={({ field: { onChange, value, name, ...rest } }) => (
            <Input
              name={name}
              value={value}
              mask={'br_postal_code'}
              onChange={onChange}
              label={i18n('checkout_addressZipCode')}
              error={!!errors?.billingAddress?.zipCode}
              helperText={errors?.billingAddress?.zipCode?.message as string}
              {...rest}
            />
          )}
        />
      ) : null}

      <Controller
        name='cardNumber'
        control={control}
        render={({ field: { onChange, value, name, ...rest } }) => (
          <Input
            name={name}
            mask='card_number'
            value={value}
            onChange={onChange}
            label={i18n('checkout_cardNumber')}
            error={!!getCardNumberError()}
            helperText={
              getCardNumberError() ?? (
                <>
                  <Brands>
                    {paymentOption.brands?.map((brand) => (
                      <CardBrandArt code={brand.code} key={brand.code} />
                    ))}
                  </Brands>
                </>
              )
            }
            {...rest}
          />
        )}
      />

      <Controller
        name='securityCode'
        control={control}
        render={({ field: { onChange, value, name, ...rest } }) => (
          <Input
            name={name}
            mask='security_card_code'
            value={value}
            onChange={onChange}
            label={i18n('checkout_securityCode')}
            error={!!errors[name]}
            helperText={errors[name]?.message as string}
            {...rest}
          />
        )}
      />

      <Controller
        name='validity'
        control={control}
        render={({ field: { onChange, value, name, ...rest } }) => (
          <Input
            name={name}
            mask='payment_card_validity'
            value={value}
            onChange={onChange}
            label={i18n('checkout_valid_date')}
            error={!!errors[name]}
            helperText={errors[name]?.message as string}
            placeholder={i18n('direct_link_card_form_validity_placeholder')}
            {...rest}
          />
        )}
      />

      <Controller
        name='installments'
        control={control}
        render={({ field: { onChange, value, name, ...rest } }) => (
          <Select
            name={name}
            value={value}
            onChange={onChange}
            label={i18n('checkout_paymentIn')}
            error={!!errors[name]}
            helperText={errors[name]?.message as string}
            {...rest}
          >
            {paymentOption?.installmentOptions.map(({ division, label }) => (
              <option key={division} value={division}>
                {label}
              </option>
            ))}
          </Select>
        )}
      />

      {requiresExternalAuthentication ? (
        <BillingAddressSection>
          <h5>{i18n('checkout_addressTitle')}</h5>

          <Controller
            name='billingAddress.zipCode'
            control={control}
            render={({ field: { onChange, onBlur, value, name, ...rest } }) => (
              <Input
                name={name}
                value={value}
                mask={!isForeignBuyer ? 'br_postal_code' : undefined}
                onChange={onChange}
                onBlur={(event) => {
                  onBlur();
                  getAddressByCep(event.target.value ?? '');
                }}
                label={i18n('checkout_addressZipCode')}
                error={!!errors?.billingAddress?.zipCode}
                helperText={errors?.billingAddress?.zipCode?.message as string}
                {...rest}
              />
            )}
          />

          <Controller
            name='billingAddress.state'
            control={control}
            render={({ field: { onChange, value, name, ...rest } }) => (
              <Input
                name={name}
                value={value}
                onChange={onChange}
                label={i18n('checkout_addressState')}
                error={!!errors?.billingAddress?.state}
                helperText={errors?.billingAddress?.state?.message as string}
                {...rest}
              />
            )}
          />

          <Controller
            name='billingAddress.city'
            control={control}
            render={({ field: { onChange, value, name, ...rest } }) => (
              <Input
                name={name}
                value={value}
                onChange={onChange}
                label={i18n('checkout_addressCity')}
                error={!!errors?.billingAddress?.city}
                helperText={errors?.billingAddress?.city?.message as string}
                {...rest}
              />
            )}
          />

          <Controller
            name='billingAddress.district'
            control={control}
            render={({ field: { onChange, value, name, ...rest } }) => (
              <Input
                name={name}
                value={value}
                onChange={onChange}
                label={i18n('checkout_addressDistrict')}
                error={!!errors?.billingAddress?.district}
                helperText={errors?.billingAddress?.district?.message as string}
                {...rest}
              />
            )}
          />

          <Controller
            name='billingAddress.street'
            control={control}
            render={({ field: { onChange, value, name, ...rest } }) => (
              <Input
                name={name}
                value={value}
                onChange={onChange}
                label={i18n('checkout_addressStreet')}
                error={!!errors?.billingAddress?.street}
                helperText={errors?.billingAddress?.street?.message as string}
                {...rest}
              />
            )}
          />

          <Controller
            name='billingAddress.number'
            control={control}
            render={({ field: { onChange, value, name, ...rest } }) => (
              <Input
                name={name}
                value={value}
                onChange={onChange}
                label={i18n('checkout_addressNumber')}
                error={!!errors?.billingAddress?.number}
                helperText={errors?.billingAddress?.number?.message as string}
                {...rest}
              />
            )}
          />

          <Controller
            name='billingAddress.complement'
            control={control}
            render={({ field: { onChange, value, name, ...rest } }) => (
              <Input
                name={name}
                value={value}
                onChange={onChange}
                label={i18n('checkout_addressComplement')}
                error={!!errors?.billingAddress?.complement}
                helperText={errors?.billingAddress?.complement?.message as string}
                {...rest}
              />
            )}
          />
        </BillingAddressSection>
      ) : null}

      <Button type='submit' fullWidth>
        {i18n('direct_link_card_form_submit')} <MdOutlineCheckCircle />
      </Button>

      {isLoading ? <BackdropLoading /> : null}
    </form>
  );
};

const BillingAddressSection = styled.div`
  h5 {
    ${BodyRegularMedium};
    color: ${LayoutColors.blackDark};
    margin: 16px 0 8px;
  }
`;

// Moved here from the old card-form/components/styles.ts, whose only other exports were orphaned.
const Brands = styled.div`
  display: flex;
  gap: 0.25rem;
  margin-top: 4px;
  img {
    width: 25px;
    margin-right: 4px;
  }
`;

export default DirectLinkCardForm;
